import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'

const API_BASE = import.meta.env.DEV ? '/api' : 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'

export function CreateOrganizationPage() {
  const navigate = useNavigate()
  const { firebaseUser } = useAuth()
  const [organizationName, setOrganizationName] = useState('')
  // 入会時に選択するプラン（決済連携まではトライアル運用）
  const [selectedPlan, setSelectedPlan] = useState<'free' | 'standard' | 'business' | 'business_plus'>('free')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [password, setPassword] = useState('')
  const [isPasswordVerified, setIsPasswordVerified] = useState(false)
  const [passwordError, setPasswordError] = useState('')

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (password === 'takamasa1985y') {
      setIsPasswordVerified(true)
      setPasswordError('')
    } else {
      setPasswordError('パスワードが正しくありません')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!organizationName.trim()) {
      setError('組織名を入力してください')
      return
    }

    if (organizationName.trim().length > 100) {
      setError('組織名は100文字以内で入力してください')
      return
    }

    try {
      setLoading(true)

      if (!firebaseUser) {
        setError('ログインが必要です')
        return
      }

      const token = await firebaseUser.getIdToken()
      const response = await fetch(`${API_BASE}/v1/organizations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: organizationName.trim(), plan: selectedPlan })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || '組織の作成に失敗しました')
      }

      const data = await response.json()
      console.log('✅ Organization created:', data)

      // トークンを再取得してCustom Claimsを更新
      await firebaseUser.getIdToken(true)

      // ページをリロードして新しいClaimsを反映
      window.location.href = '/'

    } catch (error) {
      console.error('❌ Failed to create organization:', error)
      setError(error instanceof Error ? error.message : '組織の作成に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  // パスワード未確認の場合はパスワード入力画面を表示
  if (!isPasswordVerified) {
    return (
      <div className="flex-1 w-full overflow-y-auto flex items-center justify-center" style={{ background: 'var(--color-surface)' }}>
        <div className="w-full px-6 py-12" style={{ maxWidth: 420 }}>
          <div className="card w-full">
            <div className="text-center" style={{ marginBottom: 'var(--space-6)' }}>
              <span className="brand-logo text-2xl">BluePaper</span>
              <h2 className="text-xl font-semibold text-primary" style={{ marginTop: 'var(--space-4)' }}>
                組織作成（ベータ版）
              </h2>
              <p className="text-sm text-secondary" style={{ marginTop: 'var(--space-2)' }}>
                この機能は現在ベータテスト中です
              </p>
            </div>

            <form onSubmit={handlePasswordSubmit}>
              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  アクセスパスワード
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`form-input${passwordError ? ' error' : ''}`}
                  placeholder="パスワードを入力"
                />
                {passwordError && (
                  <p className="error-message">{passwordError}</p>
                )}
              </div>

              <div className="alert warning" style={{ marginBottom: 'var(--space-5)' }}>
                <div className="alert-body">
                  <p className="text-sm">
                    この機能は現在限定公開中です。アクセスパスワードをお持ちの方のみご利用いただけます。
                  </p>
                </div>
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-lg w-full"
              >
                確認
              </button>

              <div className="text-center" style={{ marginTop: 'var(--space-4)' }}>
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="btn btn-ghost btn-sm"
                >
                  戻る
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 w-full overflow-y-auto flex items-center justify-center" style={{ background: 'var(--color-surface)' }}>
      <div className="w-full px-6 py-12" style={{ maxWidth: 420 }}>
        <div className="card w-full">
          <div className="text-center" style={{ marginBottom: 'var(--space-6)' }}>
            <span className="brand-logo text-2xl">BluePaper</span>
            <h2 className="text-xl font-semibold text-primary" style={{ marginTop: 'var(--space-4)' }}>
              組織を作成
            </h2>
            <p className="text-sm text-secondary" style={{ marginTop: 'var(--space-2)' }}>
              新しい組織を作成して、チームでBluePaperを使い始めましょう
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="organizationName" className="form-label">
                組織名 <span className="text-danger">*</span>
              </label>
              <input
                id="organizationName"
                name="organizationName"
                type="text"
                required
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                className={`form-input${error ? ' error' : ''}`}
                placeholder="例: 株式会社サンプル"
                maxLength={100}
              />
              <p className="form-hint">
                {organizationName.length}/100文字
              </p>
              {error && (
                <p className="error-message">{error}</p>
              )}
            </div>

            {/* プラン選択 */}
            <div className="form-group">
              <label className="form-label">プランを選択</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))', gap: 8 }}>
                {([
                  { id: 'free', name: '無料', price: '¥0', desc: '3人まで・bot月3時間' },
                  { id: 'standard', name: 'スタンダード', price: '¥1,480/人', desc: '2人〜・月8時間×人数' },
                  { id: 'business', name: 'ビジネス', price: '¥3,980/人', desc: '3人〜・月20時間×人数' },
                  { id: 'business_plus', name: 'ビジネスプラス', price: '¥9,800/人', desc: '5人〜・月60時間×人数' }
                ] as const).map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setSelectedPlan(p.id)}
                    className="card"
                    style={{
                      padding: 12,
                      textAlign: 'left',
                      cursor: 'pointer',
                      marginBottom: 0,
                      ...(selectedPlan === p.id
                        ? { borderColor: 'var(--color-accent)', borderWidth: 2, background: 'var(--color-accent-light, #eef1f7)' }
                        : {})
                    }}
                  >
                    <div className="text-sm font-semibold">{p.name}</div>
                    <div className="text-sm">{p.price}<span className="text-xs text-secondary">/月</span></div>
                    <div className="text-xs text-tertiary">{p.desc}</div>
                  </button>
                ))}
              </div>
              <p className="form-hint">
                有料プランは決済連携の準備が整うまでトライアルとして無料でご利用いただけます。作成後に設定からいつでも変更できます。
              </p>
            </div>

            <div className="alert info" style={{ marginBottom: 'var(--space-5)' }}>
              <div className="alert-body">
                <p className="alert-title text-sm">組織作成後の流れ</p>
                <ul className="text-sm space-y-1">
                  <li>• あなたが組織の管理者になります</li>
                  <li>• メンバーを招待できます</li>
                  <li>• 議事録の作成・管理ができます</li>
                  <li>• フォルダで議事録を整理できます</li>
                </ul>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !organizationName.trim()}
              className="btn btn-primary btn-lg w-full"
            >
              {loading ? (
                <>
                  <span className="spinner sm" style={{ borderTopColor: 'var(--color-text-inverse)' }} />
                  作成中...
                </>
              ) : (
                '組織を作成'
              )}
            </button>

            <div className="text-center" style={{ marginTop: 'var(--space-4)' }}>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="btn btn-ghost btn-sm"
              >
                キャンセル
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
